import { Form } from 'factorial-form'
import { ContractVersionsPageContractVersionCompensationFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { dateToString, i18nDateToString } from 'modules/core/lib/dates'
import { dateUIToBackend } from 'modules/core/lib/dates'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import Compensation from 'modules/contracts/store/models/Compensation'
import type ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'
import { amountInCent } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/amountInCents'
import type { Policies } from 'modules/payroll_bundle/store/collections/policies'

declare type Values = {
  [key: string]: string | number
}

export const CompensationTypes = (): Record<string, string> => ({
  fixed: i18n.t('compensation.types.fixed'),
  per_worked_day: i18n.t('compensation.types.per_worked_day'),
  per_worked_day_short_version: i18n.t('compensation.types.per_worked_day_short_version'),
  per_worked_hour: i18n.t('compensation.types.per_worked_hour'),
  undefined: i18n.t('compensation.types.undefined'),
  up_to: i18n.t('compensation.types.up_to'),
})

export const additionalCompensationType = (type: string | undefined | null) => {
  const compensationTypes = CompensationTypes()
  if (!type) return compensationTypes.undefined

  return (compensationTypes[type] || compensationTypes.undefined)?.toLowerCase()
}

export const getDescriptionByType = ({
  compensation,
  employeeLegalEntityId,
  legalEntityCurrency,
  locale,
}: {
  compensation: ContractVersionsPageContractVersionCompensationFragment
  employeeLegalEntityId: number
  legalEntityCurrency: string
  locale: string
}) => {
  const count = compensation.recurrenceCount
  const compensationType = additionalCompensationType(compensation.compensationType)
  const amount = getUnitValue(compensation, legalEntityCurrency, locale)
  const yearlyAmount = formatCurrency(
    compensation.yearlyEquivalentAmount || 0,
    undefined,
    null,
    employeeLegalEntityId
  )
  const recurrence = compensation.syncWithSupplements
    ? count
      ? i18n.tp('contract_versions.forms.additional_compensation.modal.every_n_payroll_periods', {
          count: Number(count),
        })
      : i18n.t('common.no_recurrence')
    : compensation.recurrence
    ? Compensation.recurrences()[compensation.recurrence]
    : i18n.t('common.no_recurrence')
  const year = i18n.t('contract_versions.compensation.additional_compensations.year')
  const compensationTypes = CompensationTypes()

  switch (compensationType) {
    case compensationTypes.undefined?.toLowerCase():
      return `${compensationType} / ${recurrence}`
    case compensationTypes.fixed?.toLowerCase():
      if (!compensation.yearlyEquivalentAmount) return `${amount} / ${recurrence}`
      return `${amount} / ${recurrence} -> ${yearlyAmount} / ${year}`
    case compensationTypes.per_worked_day?.toLowerCase():
      if (!compensation.yearlyEquivalentAmount) return `${amount} / ${recurrence}`
      return `${amount} / ${recurrence} -> ${yearlyAmount} / ${year}`
    case compensationTypes.up_to?.toLowerCase():
      if (!compensation.yearlyEquivalentAmount)
        return `${compensationType} ${amount} / ${recurrence}`
      return `${compensationType} ${amount} / ${recurrence} -> ${yearlyAmount} / ${year}`
    default:
      return `${compensationType} ${amount}`
  }
}

const getUnitValue = (
  compensation: ContractVersionsPageContractVersionCompensationFragment,
  legalEntityCurrency: string,
  locale: string,
  type?: string | undefined | null
) => {
  const CENTS = 100
  const yearlyEquivalentAmount = compensation?.yearlyEquivalentAmount

  if (type == 'yearly_equivalent' && yearlyEquivalentAmount == null) return null

  const compensationAmount = compensation?.amount || 0
  const amount = type == 'yearly_equivalent' ? yearlyEquivalentAmount : compensationAmount
  const isTypeUnits = compensation?.unit === 'units'

  if (isTypeUnits) return i18n.tp('compensation.unit.units', { count: Number(amount) / CENTS })

  return MoneyWithCurrency({
    amount: Number(amount),
    currency: legalEntityCurrency,
    optionalDecimals: true,
    locale,
  })
}

export const compensationDefaultAttributes = ({
  contractVersionId,
  policies,
  contractVersionPolicy,
  hasPayrollBundle,
}: {
  contractVersionId?: number | null
  policies: Policies
  contractVersionPolicy: ContractVersionPayrollPolicy | null
  hasPayrollBundle: boolean
}) => {
  const attributes = {
    contract_version_id: contractVersionId ? contractVersionId : null,
    contracts_taxonomy_id: null,
    description: null,
    compensation_type: Compensation.types.undefined,
    calculation: Compensation.calculations.current_period,
    amount: null,
    unit: 'money',
    sync_with_supplements: hasPayrollBundle,
    recurrence: null,
    first_payment_on: i18nDateToString(moment().startOf('month'), 'backend'),
    time_condition: null,
    minimum_amount_of_hours: null,
  }
  const payrollPolicyId = contractVersionPolicy?.get('payroll_policy_id')
  const rule = policies.get(payrollPolicyId)?.recurrenceRule()
  // https://github.com/jakubroztocil/rrule#rruleprototypebeforedt-incfalse
  const startsOn = rule?.before(new Date(), true)

  return {
    ...attributes,
    sync_with_supplements: hasPayrollBundle && !!contractVersionPolicy,
    payroll_policy_id: payrollPolicyId || null,
    recurrence_count: 1,
    starts_on: startsOn ? i18nDateToString(moment(startsOn), 'backend') : null,
  }
}

export function generateForm(compensation: Compensation): Form {
  const attributes = compensation.attributes
  const values = {
    id: attributes.id,
    temporal_id: attributes.temporal_id,
    contract_version_id: Number(attributes.contract_version_id),
    contracts_taxonomy_id: Number(attributes.contracts_taxonomy_id),
    description: attributes.description,
    compensation_type: attributes.compensation_type,
    calculation: attributes.calculation,
    amount: attributes.amount,
    unit: attributes.unit,
    sync_with_supplements: attributes.sync_with_supplements,
    recurrence: attributes.recurrence,
    first_payment_on: compensation.firstPaymentOn
      ? dateToString(compensation.firstPaymentOn, 'ui')
      : null,
    payroll_policy_id:
      hasFeature('payroll_bundle') && attributes.payroll_policy_id
        ? Number(attributes.payroll_policy_id)
        : null,
    recurrence_count: attributes.recurrence_count ? Number(attributes.recurrence_count) : null,
    starts_on: compensation.startsOn ? dateToString(compensation.startsOn, 'ui') : null,
    time_condition: attributes.time_condition ? attributes.time_condition : null,
    minimum_amount_of_hours: attributes.minimum_amount_of_hours
      ? Number(attributes.minimum_amount_of_hours)
      : null,
  }

  const schema = {
    temporal_id: 'string',
    contract_version_id: 'number',
    contracts_taxonomy_id: 'number',
    description: 'string',
    compensation_type: 'string',
    calculation: 'string',
    amount: 'cents',
    unit: 'string',
    sync_with_supplements: 'boolean',
    recurrence: 'string',
    first_payment_on: 'string',
    payroll_policy_id: 'number',
    recurrence_count: 'number',
    starts_on: 'string',
    time_condition: 'string',
    minimum_amount_of_hours: 'number',
  }
  return new Form(values, schema)
}

function allowsEdition(formAttributes: Values, sourceAttributes: Values): boolean {
  if (!sourceAttributes.id) return false

  const recurrence = formAttributes.recurrence == '' ? null : formAttributes.recurrence
  const payrollPolicyId = hasFeature('payroll_bundle') ? formAttributes.payroll_policy_id : null

  /**
   * We only allows editing if all keys but description have the same value.
   * We cannot allow editing otherwise as it could cause problems with persisted supplements.
   */
  return (
    formAttributes.contract_version_id == sourceAttributes.contract_version_id &&
    formAttributes.compensation_type == sourceAttributes.compensation_type &&
    formAttributes.contracts_taxonomy_id == sourceAttributes.contracts_taxonomy_id &&
    formAttributes.calculation == sourceAttributes.calculation &&
    toCents(Number(formAttributes.amount)) == sourceAttributes.amount &&
    formAttributes.unit == sourceAttributes.unit &&
    formAttributes.sync_with_supplements == sourceAttributes.sync_with_supplements &&
    recurrence == sourceAttributes.recurrence &&
    dateUIToBackend(String(formAttributes.first_payment_on)) == sourceAttributes.first_payment_on &&
    payrollPolicyId == sourceAttributes.payroll_policy_id &&
    formAttributes.recurrence_count == sourceAttributes.recurrence_count &&
    dateUIToBackend(String(formAttributes.starts_on)) == sourceAttributes.starts_on
  )
}

export function generateCompensationFromForm(
  form: Form,
  sourceCompensation: Compensation
): Compensation {
  const attributes = form.values
  const canEditCompensation = allowsEdition(attributes, sourceCompensation.attributes)

  return new Compensation({
    id: canEditCompensation ? sourceCompensation.attributes.id : undefined,
    temporal_id: attributes.temporal_id,
    contract_version_id: attributes.contract_version_id,
    description: attributes.description,
    compensation_type: attributes.compensation_type,
    contracts_taxonomy_id: attributes.contracts_taxonomy_id,
    calculation: attributes.calculation,
    amount: amountInCent(attributes.amount)?.toNumber(),
    unit: attributes.unit,
    sync_with_supplements: attributes.sync_with_supplements,
    recurrence: attributes.recurrence == '' ? null : attributes.recurrence,
    first_payment_on: dateUIToBackend(attributes.first_payment_on),
    payroll_policy_id: hasFeature('payroll_bundle') ? attributes.payroll_policy_id : null,
    recurrence_count: attributes.recurrence_count,
    starts_on: attributes.starts_on ? dateUIToBackend(attributes.starts_on) : null,
    time_condition: attributes.time_condition ? attributes.time_condition : null,
    minimum_amount_of_hours: attributes.minimum_amount_of_hours
      ? Number(attributes.minimum_amount_of_hours)
      : null,
  })
}
